import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiSlice, useLoginMutation, useLogoutMutation, useRegisterMutation } from './apiSlice.js';
import { loggedOut } from './authActions.js';
import { selectBooted, selectUser } from './authSlice.js';
import { hasModule } from '../validation/roles.js';

/**
 * The same shape the old AuthContext exposed, so components did not have to
 * change when Redux arrived: { user, loading, login, register, logout, isAdmin, can }.
 */
export function useAuth() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const booted = useSelector(selectBooted);

  const [loginMutation] = useLoginMutation();
  const [registerMutation] = useRegisterMutation();
  const [logoutMutation] = useLogoutMutation();

  // `unwrap()` rethrows the normalised { status, message, details } from
  // baseQuery, which is exactly what the forms' catch blocks already expect.
  const login = useCallback((credentials) => loginMutation(credentials).unwrap(), [loginMutation]);
  const register = useCallback((payload) => registerMutation(payload).unwrap(), [registerMutation]);

  const logout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
    } catch {
      // Network trouble on the way out. Sign out locally regardless — the
      // cookies expire on their own, and leaving the user stuck is worse.
    }
    dispatch(loggedOut());
    // Throw away every cached query, so the next person to sign in on this
    // browser never sees the last person's customer list.
    dispatch(apiSlice.util.resetApiState());
  }, [dispatch, logoutMutation]);

  return useMemo(
    () => ({
      user,
      loading: !booted,
      login,
      register,
      logout,
      isAdmin: user?.role === 'admin',
      can: (moduleName) => hasModule(user, moduleName),
    }),
    [user, booted, login, register, logout]
  );
}

export default useAuth;
